'use client';

import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Testimonial {
  quote: string;
  author: string;
  location: string;
  duration: string;
}

const testimonials: Testimonial[] = [
  {
    quote: "Been on Hinge for 3 years and was about to give up. Started using DateHack and within 3 weeks I had <strong>2 actual dates lined up</strong>. The AI suggestions actually sound like me, just more confident. My match-to-date conversion went from 1 in 100 to 1 in 10. Used to get maybe 1 date every 2 months, now getting 1 per week. Complete game changer.",
    author: "Carlos, 38",
    location: "Los Angeles",
    duration: "DateHack user for 5 weeks"
  },
  {
    quote: "The picture analyzer blew my mind. Found out my main photo was killing my chances - too serious, bad lighting. Switched to pics the AI rated higher and <strong>my matches tripled in one week</strong>. Now getting quality matches who actually message first. Should have done this months ago.",
    author: "Raj, 31",
    location: "Austin",
    duration: "DateHack user for 3 weeks"
  },
  {
    quote: "Used to send generic messages and get zero replies. DateHack taught me how to read the conversation flow and match their energy. In the past month, <strong>3 different matches asked me out first</strong>, which never happened before. My response rate went from 10% to over 60%. Friends keep asking what changed.",
    author: "Jake, 24",
    location: "Toronto",
    duration: "DateHack user for 4 weeks"
  },
  {
    quote: "The picture analysis feature is insane. It showed me exactly why I wasn't getting matches - wrong facial expressions, bad angles. Fixed my photos based on the AI feedback and went from <strong>2 matches a week to 15+</strong>. The conversation suggestions sealed the deal. Game changer.",
    author: "David, 29",
    location: "New York",
    duration: "DateHack user for 6 weeks"
  },
  {
    quote: "As an engineer, I overanalyze everything I type. DateHack fixed that. The AI gives me <strong>3 perfect options in seconds</strong> instead of me spending 20 minutes crafting one message. Met someone amazing after 2 weeks of using the app. She says I'm the best texter she's matched with.",
    author: "Arjun, 33",
    location: "San Francisco",
    duration: "DateHack user for 4 weeks"
  },
  {
    quote: "Divorced and back in the dating game after 8 years. The picture analyzer showed me I was using photos that aged me badly. New photos following the AI's advice got me <strong>5x more matches in the first week</strong>. Now dating someone I met on Bumble. This tool gave me my confidence back.",
    author: "James, 41",
    location: "London",
    duration: "DateHack user for 7 weeks"
  }
];

export default function TestimonialCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-advance carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000); // Change testimonial every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  const current = testimonials[currentIndex];

  return (
    <div className="relative w-full max-w-4xl mx-auto">
      {/* Main testimonial display */}
      <div className="glassmorphic-light rounded-xl p-8 border border-primary/10 min-h-[280px] flex flex-col items-center text-center">
        <p 
          className="text-white/80 mb-4 italic text-base leading-relaxed"
          dangerouslySetInnerHTML={{ __html: `"${current.quote}"` }}
        />
        <div className="mt-auto">
          <h4 className="font-semibold text-white">- {current.author}, {current.location}</h4>
          <p className="text-xs text-white/50 mt-1">{current.duration}</p>
        </div>
      </div>

      {/* Navigation arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 md:-translate-x-16 p-2 glassmorphic rounded-lg hover:glassmorphic-heavy transition-all group"
        aria-label="Previous testimonial"
      >
        <ChevronLeft className="h-5 w-5 text-white/60 group-hover:text-white transition-colors" />
      </button>
      
      <button
        onClick={goToNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 md:translate-x-16 p-2 glassmorphic rounded-lg hover:glassmorphic-heavy transition-all group"
        aria-label="Next testimonial"
      >
        <ChevronRight className="h-5 w-5 text-white/60 group-hover:text-white transition-colors" />
      </button>

      {/* Dots indicator */}
      <div className="flex justify-center gap-2 mt-6">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 transition-all duration-300 rounded-full ${
              index === currentIndex 
                ? 'w-8 bg-primary' 
                : 'w-2 bg-white/30 hover:bg-white/50'
            }`}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>

      {/* Disclaimer */}
      <p className="text-center text-white/40 text-xs mt-4">
        Results from real DateHack users. Individual results may vary.
      </p>
    </div>
  );
}